import React, { useContext, useEffect, useState } from "react";
import {
	Typography,
	IconButton,
	Button,
	useMediaQuery,
	useTheme,
	Card,
	CardContent,
	Chip,
	CardActions,
	Collapse,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogContentText,
	DialogActions,
	TextField,
	CircularProgress,
	Backdrop,
} from "@mui/material";
import {
	AddBox,
	Assignment,
	AssignmentLate,
	AssignmentTurnedIn,
	Check,
	Delete,
	Edit,
	ExpandLess,
	ExpandMore,
	PendingActions,
	Schedule,
	Sync,
	Undo,
} from "@mui/icons-material";
import useForm from "../../shared/hooks/form-hooks";
import Grid from "@mui/system/Unstable_Grid/Grid";
import Input from "../../shared/components/Input";
import { VALIDATOR_REQUIRE } from "../../shared/util/validators";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import AdapterJalaali from "@date-io/jalaali";
import jMoment from "moment-jalaali";

import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { useSnackbar } from "notistack";

export const Checklist = () => {
	const theme = useTheme();
	const [taskList, setTaskList] = useState([]);
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [addModalVisible, setAddModalVisible] = useState(false);
	const [selectedTask, setSelectedTask] = useState({
		id: "",
		description: "",
		date: null,
		isChecked: false,
	});
	const [isLoading, setIsLoading] = useState(true);
	const [needsRefetch, setNeedsRefetch] = useState(false);
	const auth = useContext(AuthContext);
	const { enqueueSnackbar } = useSnackbar();

	const cardOnDelete = (task) => {
		setDeleteModalVisible(true);
		setSelectedTask(task);
	};
	const cardOnEdit = (task) => {
		setEditModalVisible(true);
		setSelectedTask(task);
	};

	const modalOnClose = (c) => {
		switch (c) {
			case "add":
				setAddModalVisible(false);
				break;
			case "delete":
				setDeleteModalVisible(false);
				setSelectedTask({
					id: 0,
					description: "",
					date: null,
				});
				break;
			case "edit":
				setEditModalVisible(false);
				setSelectedTask({
					id: 0,
					description: "",
					date: null,
				});
				break;
			default:
				break;
		}
	};

	const getTaskChecklist = async () => {
		setIsLoading(true);
		await axios
			.post("/api/v1/tasks/get-tasks", undefined, {
				headers: {
					"Content-Type": "application/json",
					Authorization: auth.token,
				},
			})
			.then((response) => setTaskList(response.data.data))
			.catch((e) => {
				if (e.response.status === 404) {
					setTaskList([]);
				} else {
					const messages = e.response.data.data;
					messages.map((message) =>
						enqueueSnackbar(message, { variant: "error" })
					);
				}
			});
		setIsLoading(false);
	};
	useEffect(() => {
		getTaskChecklist();
	}, []);
	useEffect(() => {
		if (needsRefetch) getTaskChecklist();
		setNeedsRefetch(false);
	}, [needsRefetch]);

	return (
		<Grid container spacing={4}>
			<Grid xs={12} container>
				<Grid xs={6} display={"flex"} justifyContent={"flex-start"}>
					<Typography component={"h5"} variant="h5">
						چک‌لیست
					</Typography>
				</Grid>
				<Grid xs={6} display={"flex"} justifyContent={"flex-end"}>
					{useMediaQuery(theme.breakpoints.down("md")) ? (
						<IconButton
							size="small"
							aria-label="add new task"
							color="primary"
							onClick={() => setAddModalVisible(true)}
						>
							<AddBox />
						</IconButton>
					) : (
						<Button
							startIcon={<AddBox />}
							variant="contained"
							size="small"
							aria-label="add new task"
							onClick={() => setAddModalVisible(true)}
						>
							اضافه کردن کار جدید
						</Button>
					)}
					<IconButton
						size="small"
						aria-label="refetch checklist tasks"
						sx={{ ml: 2 }}
						onClick={getTaskChecklist}
					>
						<Sync fontSize="small" />
					</IconButton>
				</Grid>
			</Grid>
			<Grid
				xs={12}
				container
				spacing={4}
				sx={{ maxHeight: 420, overflowY: "auto" }}
				justifyContent={"center"}
			>
				{isLoading ? (
					<CircularProgress />
				) : taskList.length ? (
					taskList.map((task) => (
						<Grid xs={12}>
							<TaskCard
								key={task.id}
								taskId={task.id}
								theme={theme}
								description={task.description}
								dueDate={task.date}
								isChecked={task.isChecked}
								onDelete={() => cardOnDelete(task)}
								onEdit={() => cardOnEdit(task)}
								auth={auth}
								enqueueSnackbar={enqueueSnackbar}
							/>
						</Grid>
					))
				) : (
					<Typography variant="h6">موردی پیدا نشد.</Typography>
				)}
			</Grid>

			<DeleteModal
				isOpen={deleteModalVisible}
				onClose={() => modalOnClose("delete")}
				taskId={selectedTask.id}
				setNeedsRefetch={setNeedsRefetch}
				auth={auth}
				enqueueSnackbar={enqueueSnackbar}
			/>

			<EditModal
				isOpen={editModalVisible}
				onClose={() => modalOnClose("edit")}
				description={selectedTask.description}
				dueDate={new Date(parseInt(selectedTask.date))}
				taskId={selectedTask.id}
				isChecked={selectedTask.isChecked}
				setNeedsRefetch={setNeedsRefetch}
				auth={auth}
				enqueueSnackbar={enqueueSnackbar}
			/>

			<AddModal
				isOpen={addModalVisible}
				onClose={() => modalOnClose("add")}
				setNeedsRefetch={setNeedsRefetch}
				auth={auth}
				enqueueSnackbar={enqueueSnackbar}
			/>
		</Grid>
	);
};

const TaskCard = (props) => {
	const [expanded, setExpanded] = useState(false);
	const [isChecked, setIsChecked] = useState(props.isChecked ?? false);
	const [status, setStatus] = useState(
		isChecked
			? "done"
			: props.dueDate - new Date().getTime() < 0
			? "late"
			: "todo"
	);
	const [chipDetails, setChipDetails] = useState(getBadgeInfo(status));
	const underMd = useMediaQuery(props.theme.breakpoints.down("lg"));
	const [isLoading, setIsLoading] = useState(false);

	useEffect(() => {
		setChipDetails(getBadgeInfo(status));
	}, [status]);

	const markAsDone = async () => {
		setIsLoading(true);
		await axios
			.put(
				"/api/v1/tasks/update-task",
				{
					taskId: props.taskId,
					description: props.description,
					date: parseInt(props.dueDate),
					isChecked: !isChecked,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: props.auth.token,
					},
				}
			)
			.then(() => {
				setIsChecked(!isChecked);
				setStatus(
					!isChecked
						? "done"
						: props.dueDate - new Date().getTime() < 0
						? "late"
						: "todo"
				);
			})
			.catch((e) => {
				const messages = e.response.data.data;
				messages.map((message) =>
					props.enqueueSnackbar(message, { variant: "error" })
				);
			})
			.finally(() => setIsLoading(false));
	};

	return (
		<Card>
			<Backdrop
				sx={{
					color: "#fff",
					zIndex: (theme) => theme.zIndex.drawer + 1,
				}}
				open={isLoading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<CardContent>
				<Grid container padding={0}>
					<Grid xs={12} sm={9} container>
						<Grid xs={12}>
							<Typography
								variant="h6"
								textAlign={"left"}
								maxWidth={"500px"}
								noWrap
								gutterBottom
							>
								{props.description}
							</Typography>
						</Grid>
						<Grid xs={12}>
							<CardActions
								sx={{
									justifyContent: "space-between",
									flexWrap: "wrap",
									p: 0,
								}}
							>
								{underMd ? (
									<>
										<IconButton
											size="small"
											aria-label="edit task"
											color="secondary"
											onClick={() => {
												props.onEdit();
											}}
										>
											<Edit />
										</IconButton>
										<IconButton
											size="small"
											aria-label="delete task"
											color="error"
											onClick={() => props.onDelete()}
										>
											<Delete />
										</IconButton>
										<IconButton
											size="small"
											aria-label={
												status === "done"
													? "undo task"
													: "mark as done"
											}
											color={
												status === "done"
													? "primary"
													: "success"
											}
											disabled={isLoading}
											onClick={markAsDone}
										>
											{status === "done" ? (
												<Undo />
											) : (
												<Check />
											)}
										</IconButton>
										<Button
											endIcon={
												expanded ? (
													<ExpandLess />
												) : (
													<ExpandMore />
												)
											}
											variant="text"
											size="small"
											aria-label="expand description"
											color="primary"
											onClick={() =>
												setExpanded(!expanded)
											}
										>
											{expanded
												? "بستن توضیحات"
												: "مشاهده توضیحات"}{" "}
										</Button>
										{/* <IconButton
                                size="small"
                                aria-label="expand description"
                                color="primary"
                                onClick={() => setExpanded(!expanded)}
                            >
                                {expanded ? <ExpandLess /> : <ExpandMore />}
                            </IconButton> */}
									</>
								) : (
									<>
										<Button
											endIcon={<Edit />}
											variant="text"
											size="small"
											aria-label="edit task"
											color="secondary"
											onClick={() => {
												props.onEdit();
											}}
										>
											ویرایش
										</Button>
										<Button
											endIcon={<Delete />}
											variant="text"
											size="small"
											aria-label="delete task"
											color="error"
											onClick={() => props.onDelete()}
										>
											حذف
										</Button>
										<Button
											endIcon={
												status === "done" ? (
													<Undo />
												) : (
													<Check />
												)
											}
											variant="text"
											size="small"
											aria-label={
												status === "done"
													? "undo task"
													: "mark as done"
											}
											color={
												status === "done"
													? "warning"
													: "success"
											}
											onClick={markAsDone}
											disabled={isLoading}
										>
											{status === "done"
												? "تغییر وضعیت"
												: "اتمام "}
										</Button>
										<Button
											endIcon={
												expanded ? (
													<ExpandLess />
												) : (
													<ExpandMore />
												)
											}
											variant="text"
											size="small"
											aria-label="expand description"
											color="primary"
											onClick={() =>
												setExpanded(!expanded)
											}
										>
											{expanded
												? "بستن توضیحات"
												: "مشاهده توضیحات"}{" "}
										</Button>
									</>
								)}
							</CardActions>
						</Grid>
					</Grid>
					<Grid xs={12} sm={3} container spacing={1}>
						<Grid
							xs={12}
							sx={{ display: "flex", alignItems: "center" }}
						>
							<Chip
								color={"primary"}
								label={`موعد: ${jMoment(
									parseInt(props.dueDate)
								).format("jYYYY/jM/jD")}`}
								icon={<Schedule />}
								size="small"
								sx={{
									width: "100%",
									whiteSpace: "break-spaces",
									height: "auto",
								}}
							/>
						</Grid>
						<Grid
							xs={12}
							sx={{ display: "flex", alignItems: "center" }}
						>
							<Chip
								color={chipDetails.color}
								icon={chipDetails.icon}
								label={chipDetails.label}
								size="small"
								sx={{ width: "100%" }}
							/>
						</Grid>
					</Grid>
				</Grid>
			</CardContent>

			<Collapse in={expanded}>
				<CardContent>
					<Typography
						paragraph
						sx={{ textAlign: "left", fontSize: "12px", mb: 0 }}
					>
						{props.description}
					</Typography>
				</CardContent>
			</Collapse>
		</Card>
	);
};

const getBadgeInfo = (status) => {
	switch (status) {
		case "done":
			return {
				color: "success",
				label: "انجام شده",
				icon: <AssignmentTurnedIn />,
			};
		case "late":
			return {
				color: "secondary",
				label: "درحال انجام",
				icon: <PendingActions />,
			};
		case "todo":
		default:
			return {
				color: "default",
				label: "انجام نشده",
				icon: <Assignment />,
			};
	}
};

const EditModal = (props) => {
	const [formState, inputHandler, setFormData] = useForm({
		description: { value: props.description, isValid: false },
		dueDate: { value: props.dueDate, isValid: false },
	});
	const [isLoading, setIsLoading] = useState(false);
	useEffect(() => {
		setIsLoading(false);
		setFormData({
			description: {
				value: props.description,
				isValid: false,
			},
			dueDate: { value: props.dueDate, isValid: false },
		});
	}, [props.description, props.dueDate, props.isOpen, setFormData]);

	const editTask = async () => {
		setIsLoading(true);
		await axios
			.put(
				"/api/v1/tasks/update-task",
				{
					taskId: props.taskId,
					description: formState.inputs.description.value,
					date: parseInt(
						new Date(formState.inputs.dueDate.value).getTime()
					),
					isChecked: props.isChecked,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: props.auth.token,
					},
				}
			)
			.then(() => {
				props.setNeedsRefetch(true);
				props.onClose();
			})
			.catch((e) => {
				const messages = e.response.data.data;
				messages.map((message) =>
					props.enqueueSnackbar(message, { variant: "error" })
				);
			})
			.finally(() => setIsLoading(false));
	};

	return (
		<Dialog
			open={props.isOpen}
			onClose={() => {
				props.onClose();
			}}
		>
			<DialogTitle>ویرایش </DialogTitle>
			<DialogContent>
				<form
					onSubmit={() => console.log("edit task approved")}
					style={{ marginTop: "2rem" }}
				>
					<Grid container spacing={2}>
						<Grid xs={12}>
							<LocalizationProvider dateAdapter={AdapterJalaali}>
								<DatePicker
									value={parseInt(
										formState.inputs.dueDate.value
									)}
									onChange={(date) => {
										inputHandler(
											"dueDate",
											new Date(date).getTime(),
											formState.inputs.dueDate.isValid
										);
									}}
									disableMaskedInput
									label="موعد تحویل"
									renderInput={(params) => (
										<TextField
											{...params}
											fullWidth
											error={false}
											inputProps={{
												placeholder: "تاریخ تحویل ",
											}}
											size={"medium"}
											variant={"outlined"}
											value={jMoment(
												formState.inputs.dueDate.value
											).format("jYYYY/jMM/jDD")}
										/>
									)}
								/>
							</LocalizationProvider>
						</Grid>
						<Grid xs={12}>
							<Input
								id="description"
								element="input"
								label="توضیحات "
								validators={[]}
								initialValue={props.description}
								initialValid={
									formState.inputs.description.isValid
								}
								onInput={inputHandler}
							/>
						</Grid>
					</Grid>
				</form>
			</DialogContent>
			<DialogActions sx={{ py: 2, px: 3 }}>
				<Button
					onClick={() => {
						props.onClose();
					}}
					variant="outlined"
					fullWidth
					disabled={isLoading}
				>
					بستن
				</Button>
				<Button
					variant="contained"
					color="secondary"
					onClick={editTask}
					fullWidth
					disabled={isLoading}
				>
					ویرایش
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const DeleteModal = (props) => {
	const [isLoading, setIsLoading] = useState(false);
	const onClose = () => {
		props.onClose(false);
		setIsLoading(false);
	};
	const deleteTask = async () => {
		setIsLoading(true);
		await axios
			.delete("/api/v1/tasks/delete-task", {
				data: { taskId: props.taskId },
				headers: {
					"Content-Type": "application/json",
					Authorization: props.auth.token,
				},
			})
			.then(() => {
				props.setNeedsRefetch(true);
				props.onClose();
			})
			.catch((e) => {
				const messages = e.response.data.data;
				messages.map((message) =>
					props.enqueueSnackbar(message, { variant: "error" })
				);
			})
			.finally(() => setIsLoading(false));
	};
	return (
		<Dialog open={props.isOpen} onClose={onClose}>
			<DialogTitle>آیا از حذف این کار اطمینان دارید؟</DialogTitle>
			<DialogContent>
				<DialogContentText>
					با انتخاب گزینه "تایید" این کار برای همیشه حذف خواهد شد و
					امکان بازیابی آن وجود ندارد
				</DialogContentText>
				<DialogActions>
					<Button
						onClick={onClose}
						variant="outlined"
						disabled={isLoading}
					>
						انصراف
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={deleteTask}
						disabled={isLoading}
					>
						تایید
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
};

const AddModal = (props) => {
	const [formState, inputHandler, setFormData] = useForm({
		description: { value: "", isValid: false },
		dueDate: { value: "", isValid: false },
	});
	const [isLoading, setIsLoading] = useState(false);
	const onClose = () => {
		props.onClose();
		setFormData({
			description: { value: "", isValid: false },
			dueDate: { value: "", isValid: false },
		});
	};

	const createTask = async () => {
		setIsLoading(true);
		await axios
			.post(
				"/api/v1/tasks/create-task",
				{
					description: formState.inputs.description.value,
					date: parseInt(formState.inputs.dueDate.value),
					isChecked: false,
				},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: props.auth.token,
					},
				}
			)
			.then(() => {
				props.setNeedsRefetch(true);
				onClose();
			})
			.catch((e) => {
				const messages = e.response.data.data;
				messages.map((message) =>
					props.enqueueSnackbar(message, { variant: "error" })
				);
			})
			.finally(() => setIsLoading(false));
	};
	return (
		<Dialog open={props.isOpen} onClose={onClose}>
			<DialogTitle>ثبت کار جدید </DialogTitle>
			<DialogContent>
				<form
					onSubmit={() => console.log("adding task approved")}
					style={{ marginTop: "2rem" }}
				>
					<Grid container spacing={2}>
						<Grid xs={12}>
							<LocalizationProvider dateAdapter={AdapterJalaali}>
								<DatePicker
									value={formState.inputs.dueDate.value}
									onChange={(date) => {
										inputHandler(
											"dueDate",
											new Date(date).getTime(),
											formState.inputs.dueDate.isValid
										);
									}}
									label="موعد تحویل"
									disableMaskedInput
									renderInput={(params) => (
										<TextField
											{...params}
											fullWidth
											error={false}
											inputProps={{
												placeholder: "تاریخ تحویل ",
											}}
											size={"medium"}
											variant={"outlined"}
											value={
												formState.inputs.dueDate.value
													? jMoment(
															formState.inputs
																.dueDate.value
													  ).format("jYYYY/jMM/jDD")
													: ""
											}
										/>
									)}
								/>
							</LocalizationProvider>
						</Grid>
						<Grid xs={12}>
							<Input
								id="description"
								element="textarea"
								label="توضیحات "
								errorText="لطفاً توضیحات  را وارد کنید"
								validators={[VALIDATOR_REQUIRE]}
								initialValue={
									formState.inputs.description.value
								}
								initialValid={
									formState.inputs.description.isValid
								}
								onInput={inputHandler}
							/>
						</Grid>
					</Grid>
				</form>
			</DialogContent>
			<DialogActions sx={{ py: 2, px: 3 }}>
				<Button
					onClick={() => {
						props.onClose();
					}}
					variant="outlined"
					fullWidth
					disabled={isLoading}
				>
					بستن
				</Button>
				<Button
					variant="contained"
					color={"primary"}
					onClick={createTask}
					fullWidth
					disabled={
						isLoading ||
						!formState.inputs.dueDate.value ||
						!formState.inputs.description.value
					}
				>
					ثبت
				</Button>
			</DialogActions>
		</Dialog>
	);
};
